/**
 * Core-100 R2 — CORE-BM GAP-006 / GAP-002: a participant can see their own participation.
 *
 * Status, when the agreement was signed, when the period ends, the next readiness check-in, the
 * check-ins already given, what the schedule recorded (check-in due / period ended) and — for a
 * founding participant — their certificate, opened through a short-lived link minted only for a
 * member of this shop. Hidden entirely for a shop that is not in the beta.
 */
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface BetaShop {
  is_beta_tester: boolean | null;
  beta_status: string | null;
  beta_signed_at: string | null;
  beta_expires_at: string | null;
  beta_next_checkin_at: string | null;
  beta_checkin_count: number | null;
  is_founding_beta: boolean | null;
  /** The shop's zone (mirrors shop_profile.time_zone since R4 X3). */
  time_zone?: string | null;
}

export const BETA_STATUS_LABEL: Record<string, string> = {
  active: 'Active',
  extended: 'Extended',
  completed_awaiting_decision: 'Period ended — OptiBay will be in touch',
  go_live_approved: 'Approved to go live',
  ended: 'Ended',
};

/**
 * Core-100 R5 (CORE-BM Rel): a participation date is the SHOP's day, the same day the period-ending notice states
 * (run_beta_lifecycle renders it in the shop zone) — never the viewer's browser zone, which differs near midnight.
 */
export const shopDay = (iso: string | null, timeZone: string | null | undefined) => {
  if (!iso) return '—';
  const at = new Date(iso);
  if (timeZone) {
    try {
      return at.toLocaleDateString(undefined, { timeZone });
    } catch {
      /* an unknown zone: fall through to the browser's day */
    }
  }
  return at.toLocaleDateString();
};

export function BetaParticipationCard({ shopId }: { shopId: string }) {
  const { data } = useQuery({
    queryKey: ['beta-participation', shopId],
    queryFn: async () => {
      const [{ data: shop }, { data: checkins }, { data: events }] = await Promise.all([
        supabase.from('shops')
          .select('is_beta_tester, beta_status, beta_signed_at, beta_expires_at, beta_next_checkin_at, beta_checkin_count, is_founding_beta, time_zone')
          .eq('id', shopId).maybeSingle(),
        supabase.from('beta_checkins').select('id, created_at, stability_rating, blocking').eq('shop_id', shopId).order('created_at', { ascending: false }).limit(10),
        supabase.from('beta_lifecycle_events' as never).select('kind, due_at').eq('shop_id', shopId).order('due_at', { ascending: false }).limit(10),
      ]);
      return {
        shop: shop as BetaShop | null,
        checkins: (checkins ?? []) as Array<{ id: string; created_at: string; stability_rating: number | null; blocking: boolean | null }>,
        events: ((events ?? []) as unknown) as Array<{ kind: string; due_at: string }>,
      };
    },
  });
  const shop = data?.shop;
  if (!shop?.is_beta_tester) return null;
  const day = (iso: string | null) => shopDay(iso, shop.time_zone);

  const openCertificate = async () => {
    const { data: res, error } = await supabase.functions.invoke('generate-beta-certificate', { body: { shopId } });
    const url = (res as { signed_url?: string | null } | null)?.signed_url;
    if (error || !url) { toast.error('The certificate is not available yet'); return; }
    window.open(url, '_blank', 'noopener');
  };

  return (
    <Card data-testid="beta-participation">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          Beta participation <Badge variant="outline">{BETA_STATUS_LABEL[shop.beta_status ?? ''] ?? shop.beta_status ?? 'Unknown'}</Badge>
        </CardTitle>
        <CardDescription>Your participation as OptiBay records it.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <div className="grid grid-cols-2 gap-2">
          <div><p className="text-xs text-muted-foreground">Agreement signed</p><p>{shop.beta_signed_at ? day(shop.beta_signed_at) : 'Not signed yet'}</p></div>
          <div><p className="text-xs text-muted-foreground">Period ends</p><p>{day(shop.beta_expires_at)}</p></div>
          <div><p className="text-xs text-muted-foreground">Next check-in</p><p>{day(shop.beta_next_checkin_at)}</p></div>
          <div><p className="text-xs text-muted-foreground">Check-ins given</p><p>{shop.beta_checkin_count ?? data?.checkins.length ?? 0}</p></div>
        </div>
        {(data?.checkins.length ?? 0) > 0 && (
          <ul className="space-y-1" data-testid="beta-checkin-history">
            {data!.checkins.map((c) => (
              <li key={c.id} className="text-xs text-muted-foreground">
                {day(c.created_at)}{c.stability_rating !== null ? ` · stability ${c.stability_rating}/5` : ''}{c.blocking ? ' · something was blocking you' : ''}
              </li>
            ))}
          </ul>
        )}
        {(data?.events.length ?? 0) > 0 && (
          <ul className="space-y-1" data-testid="beta-lifecycle-events">
            {data!.events.map((e) => (
              <li key={`${e.kind}-${e.due_at}`} className="text-xs text-muted-foreground">
                {/* Core-100 R4: 'period_ending' (the advance warning) is its own event, never a "check-in". */}
                {e.kind === 'period_ended' ? 'Period ended' : e.kind === 'period_ending' ? 'Period ends' : 'Check-in due'} · {day(e.due_at)}
              </li>
            ))}
          </ul>
        )}
        {shop.is_founding_beta && shop.beta_signed_at && (
          <Button variant="outline" size="sm" onClick={() => void openCertificate()}>Open your founding beta certificate</Button>
        )}
      </CardContent>
    </Card>
  );
}
